/**
 * PLW Wishlist (CQ-1981) — a localStorage-backed wishlist (guest, v1).
 *
 * No app, no account, no backend: the wishlist is a list of product handles
 * kept in localStorage, so it works for every visitor immediately. Progressive
 * enhancement — with JS off the bookmark is inert and the page shows its empty
 * state; nothing server-rendered depends on it.
 *
 * Three custom elements share one store (this module loads once — the
 * <script type="module"> tag is deduped by URL across the snippets that emit it):
 *
 *   <plw-wishlist-toggle data-handle>  a save/remove button (PDP buy box +
 *       sticky bar; also the remove control on each wishlist-page card).
 *   <plw-wishlist-count>               the header bookmark's SR-only count.
 *   <plw-wishlist-page data-item-section>  the /pages/wishlist grid — fetches
 *       each saved product's card via the Section Rendering API and injects it.
 *
 * All three react to the `plw:wishlist:change` event so every entry point stays
 * in sync within the tab; the `storage` event keeps tabs in sync too.
 */

const STORAGE_KEY = 'plw:wishlist:v1';
const CHANGE_EVENT = 'plw:wishlist:change';

/** The single source of truth — reads/writes the handle list in localStorage. */
const store = {
  /** @returns {string[]} saved product handles (newest last) */
  read() {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter((handle) => typeof handle === 'string') : [];
    } catch {
      return [];
    }
  },

  /** Persist + notify. Quota/private-mode failures degrade silently. */
  write(handles) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch {
      /* private mode / quota exceeded — keep the in-memory event so the UI still reacts */
    }
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { handles, count: handles.length } }));
  },

  has(handle) {
    return this.read().includes(handle);
  },

  /** @returns {boolean} true if the handle is now saved, false if removed */
  toggle(handle) {
    const handles = this.read();
    const index = handles.indexOf(handle);
    if (index === -1) handles.push(handle);
    else handles.splice(index, 1);
    this.write(handles);
    return index === -1;
  },

  remove(handle) {
    this.write(this.read().filter((saved) => saved !== handle));
  },
};

// Cross-tab sync: `storage` fires in the *other* tabs when one tab writes.
window.addEventListener('storage', (event) => {
  if (event.key !== STORAGE_KEY) return;
  const handles = store.read();
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { handles, count: handles.length } }));
});

/**
 * <plw-wishlist-toggle data-handle="…"> — wraps a <button>. Click toggles the
 * product; the button reflects saved state via aria-pressed + a data-saved flag
 * (CSS fills the bookmark) and swaps its accessible name/tooltip.
 */
class PlwWishlistToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.handle = this.dataset.handle;
    if (!this.button || !this.handle) return;

    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.button.addEventListener('click', this.onClick);
    document.addEventListener(CHANGE_EVENT, this.onChange);
    this.render(store.has(this.handle));
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
    document.removeEventListener(CHANGE_EVENT, this.onChange);
  }

  onClick(event) {
    event.preventDefault();
    this.render(store.toggle(this.handle));
  }

  onChange() {
    this.render(store.has(this.handle));
  }

  render(saved) {
    this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    this.button.toggleAttribute('data-saved', saved);
    const label = saved ? this.button.dataset.labelRemove : this.button.dataset.labelSave;
    if (label) {
      this.button.setAttribute('aria-label', label);
      if (this.button.hasAttribute('data-tooltip')) this.button.setAttribute('data-tooltip', label);
    }
  }
}

/**
 * <plw-wishlist-count> — the header bookmark indicator. Exposes the count to
 * assistive tech only (no visible dot or badge — the dot was removed per
 * client QA 2026-08-14; a numeric badge would break the type lockdown).
 */
class PlwWishlistCount extends HTMLElement {
  connectedCallback() {
    this.onChange = this.onChange.bind(this);
    document.addEventListener(CHANGE_EVENT, this.onChange);
    this.render(store.read().length);

    // Cart updates morph the whole header section in 'full' mode, re-syncing
    // this subtree to the server HTML (SR text empty) WITHOUT reconnecting
    // the element — which used to blank the state until the next wishlist
    // mutation. Re-assert the store state whenever the subtree is touched;
    // render() converges (it only mutates when the DOM disagrees).
    this.observer = new MutationObserver(() => this.render(store.read().length));
    this.observer.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['hidden'],
    });
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.onChange);
    this.observer?.disconnect();
    this.observer = null;
  }

  onChange(event) {
    this.render(event.detail?.count ?? store.read().length);
  }

  render(count) {
    // Query fresh each time — a morph may have replaced the nodes.
    const srText = this.querySelector('[data-wishlist-count-sr]');
    if (srText) {
      const text = srText.dataset.template?.replace('[count]', count) || '';
      if (srText.textContent !== text) srText.textContent = text;
    }
  }
}

/**
 * <plw-wishlist-page data-item-section="…"> — the /pages/wishlist grid.
 * On connect it fetches each saved product's card (Section Rendering API,
 * `/products/{handle}?section_id=…`) and injects it. Removals (from a card's
 * own toggle or another tab) prune the matching node in place — no refetch.
 */
class PlwWishlistPage extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-wishlist-grid]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.itemSection = this.dataset.itemSection;
    this.onChange = this.onChange.bind(this);
    document.addEventListener(CHANGE_EVENT, this.onChange);
    this.load();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.onChange);
    this.controller?.abort();
  }

  /** Prune nodes for handles that are no longer saved (cheap, no refetch). */
  onChange() {
    if (!this.grid) return;
    const saved = new Set(store.read());
    for (const item of this.grid.querySelectorAll('[data-wishlist-item]')) {
      if (!saved.has(item.dataset.handle)) item.remove();
    }
    this.toggleEmpty(this.grid.children.length === 0);
  }

  toggleEmpty(isEmpty) {
    if (this.empty) this.empty.hidden = !isEmpty;
  }

  async load() {
    const handles = store.read();
    if (handles.length === 0) {
      this.toggleEmpty(true);
      return;
    }
    this.toggleEmpty(false);
    if (!this.grid || !this.itemSection) return;

    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    const root = window.Shopify?.routes?.root || '/';

    try {
      const results = await Promise.all(
        handles.map(async (handle) => {
          try {
            const response = await fetch(`${root}products/${handle}?section_id=${this.itemSection}`, { signal });
            return response.ok ? await response.text() : null;
          } catch {
            return null;
          }
        })
      );
      if (signal.aborted) return;

      const fragment = document.createDocumentFragment();
      for (const html of results) {
        if (!html) continue;
        const item = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-wishlist-item]');
        if (item) fragment.appendChild(document.importNode(item, true));
      }
      this.grid.replaceChildren(fragment);
      this.toggleEmpty(this.grid.children.length === 0);
    } catch (error) {
      if (!signal.aborted) console.error('[plw-wishlist] failed to load saved products', error);
    }
  }
}

if (!customElements.get('plw-wishlist-toggle')) customElements.define('plw-wishlist-toggle', PlwWishlistToggle);
if (!customElements.get('plw-wishlist-count')) customElements.define('plw-wishlist-count', PlwWishlistCount);
if (!customElements.get('plw-wishlist-page')) customElements.define('plw-wishlist-page', PlwWishlistPage);
